// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

/**
 * Reference tones — the "pitch pipe". Two jobs:
 *  1. `renderReferenceTone` synthesises a pleasant sustained note into a
 *     Float32 buffer (pure maths → the downloadable WAV artefact).
 *  2. `TonePlayer` plays a single note live through Web Audio for tuning by ear.
 */

/** Per-note duration and fades for the rendered pitch pipe. */
const NOTE_SECONDS = 1.6;
const FADE_SECONDS = 0.04;
const GAP_SECONDS = 0.12;

/**
 * Render one sustained note to a mono Float32 buffer at `sampleRate`.
 * A soft organ-like timbre (fundamental + a little octave and twelfth) with
 * short raised-cosine fades so it never clicks. Pure — used for the WAV export
 * and unit-testable.
 */
export function renderReferenceTone(hz: number, sampleRate: number, seconds = NOTE_SECONDS): Float32Array {
  const n = Math.max(1, Math.floor(seconds * sampleRate));
  const out = new Float32Array(n);
  const fade = Math.max(1, Math.floor(FADE_SECONDS * sampleRate));
  const twoPi = 2 * Math.PI;
  for (let i = 0; i < n; i++) {
    const t = i / sampleRate;
    const phase = twoPi * hz * t;
    // Fundamental plus quiet harmonics for a rounder, pipe-like tone.
    let s = Math.sin(phase) + 0.28 * Math.sin(2 * phase) + 0.12 * Math.sin(3 * phase);
    s *= 0.5; // headroom against the summed harmonics
    // Raised-cosine attack/release envelope.
    let env = 1;
    if (i < fade) env = 0.5 - 0.5 * Math.cos((Math.PI * i) / fade);
    else if (i > n - fade) env = 0.5 - 0.5 * Math.cos((Math.PI * (n - i)) / fade);
    out[i] = s * env;
  }
  return out;
}

/**
 * Render a sequence of notes (Hz) back-to-back with small gaps into one buffer.
 * This is the instrument's whole pitch pipe.
 */
export function renderReferenceSequence(freqs: number[], sampleRate: number): Float32Array {
  const noteLen = Math.floor(NOTE_SECONDS * sampleRate);
  const gapLen = Math.floor(GAP_SECONDS * sampleRate);
  const total = freqs.length * noteLen + Math.max(0, freqs.length - 1) * gapLen;
  const out = new Float32Array(total);
  let offset = 0;
  for (let k = 0; k < freqs.length; k++) {
    const note = renderReferenceTone(freqs[k], sampleRate);
    out.set(note, offset);
    offset += noteLen + gapLen;
  }
  return out;
}

type Ctor = { new (): AudioContext };

/** Live single-note reference playback. Reuses one AudioContext. */
export class TonePlayer {
  private ctx: AudioContext | null = null;
  private osc: OscillatorNode | null = null;
  private gain: GainNode | null = null;
  private currentHz = 0;

  private ensureCtx(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const w = window as unknown as { AudioContext?: Ctor; webkitAudioContext?: Ctor };
    const C = w.AudioContext || w.webkitAudioContext;
    if (!C) return null;
    this.ctx = new C();
    return this.ctx;
  }

  get playing(): boolean {
    return this.osc !== null;
  }

  get hz(): number {
    return this.currentHz;
  }

  /** Start (or retune) the reference tone at `hz`. Returns false if unsupported. */
  play(hz: number): boolean {
    const ctx = this.ensureCtx();
    if (!ctx) return false;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    if (!this.osc) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      gain.gain.value = 0;
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      // Short attack to avoid a click.
      gain.gain.setTargetAtTime(0.18, ctx.currentTime, 0.02);
      this.osc = osc;
      this.gain = gain;
    }
    this.osc.frequency.setTargetAtTime(hz, ctx.currentTime, 0.01);
    this.currentHz = hz;
    return true;
  }

  stop(): void {
    const ctx = this.ctx;
    if (!ctx || !this.osc || !this.gain) return;
    const now = ctx.currentTime;
    this.gain.gain.setTargetAtTime(0, now, 0.03);
    const osc = this.osc;
    const gain = this.gain;
    this.osc = null;
    this.gain = null;
    this.currentHz = 0;
    setTimeout(() => {
      try {
        osc.stop();
        osc.disconnect();
        gain.disconnect();
      } catch {
        /* already gone */
      }
    }, 120);
  }

  dispose(): void {
    this.stop();
    if (this.ctx && this.ctx.state !== 'closed') this.ctx.close().catch(() => {});
    this.ctx = null;
  }
}
